import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../../utils/api";
import { VELVET_ACCENT, VELVET_INK, VELVET_WARM } from "../../utils/brandLogo";

export type JuCaseStatsRange = "week" | "month" | "6m" | "1y" | "all";

const RANGES: { id: JuCaseStatsRange; label: string }[] = [
  { id: "week", label: "Weekly" },
  { id: "month", label: "Monthly" },
  { id: "6m", label: "6 months" },
  { id: "1y", label: "1 year" },
  { id: "all", label: "Full time" },
];

type Point = { date: string; cases: number };

const formatLabel = (iso: string, range: JuCaseStatsRange) => {
  const d = new Date(`${iso}T12:00:00`);
  if (range === "week") {
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric" });
  }
  if (range === "month") {
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  }
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
};

const JuDailyCasesChart = ({
  userId,
  open,
  onClose,
}: {
  userId: number;
  open: boolean;
  onClose: () => void;
}) => {
  const [range, setRange] = useState<JuCaseStatsRange>("week");
  const [points, setPoints] = useState<Point[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/auth/user-cases/${userId}/case-stats`, {
          params: { range },
        });
        if (cancelled) return;
        setPoints(data?.data?.points || []);
      } catch {
        if (!cancelled) setError("Could not load the cases graph.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, userId, range]);

  const chartData = useMemo(
    () =>
      points.map((p) => ({
        ...p,
        label: formatLabel(p.date, range),
      })),
    [points, range],
  );

  const total = chartData.reduce((sum, p) => sum + p.cases, 0);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-2xl bg-white shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#EFE6DA]">
          <div>
            <div className="text-lg font-semibold" style={{ color: VELVET_INK }}>
              Cases sent
            </div>
            <div className="text-xs text-[#707070] mt-0.5">
              Daily case count since 7:00 AM UK time
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-2 py-1 text-sm text-[#707070] hover:text-[#2C2C2C]"
          >
            Close
          </button>
        </div>

        <div className="px-5 pt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1.5">
            {RANGES.map((item) => {
              const active = range === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setRange(item.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                    active
                      ? "text-white"
                      : "text-[#555555] hover:bg-[#F9F1EB]"
                  }`}
                  style={active ? { backgroundColor: VELVET_WARM } : undefined}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
          <div className="text-xs text-[#707070]">
            Total in range:{" "}
            <span className="font-semibold" style={{ color: VELVET_WARM }}>
              {total}
            </span>
          </div>
        </div>

        <div className="px-3 pb-5 pt-2 h-[340px]">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-[#888888]">
              Loading graph...
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center text-sm text-red-500">
              {error}
            </div>
          ) : !chartData.length ? (
            <div className="h-full flex items-center justify-center text-sm text-[#888888]">
              No cases in this range.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 12, right: 16, left: 0, bottom: 8 }}
              >
                <CartesianGrid stroke={VELVET_ACCENT} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#707070" }}
                  axisLine={false}
                  tickLine={false}
                  interval={chartData.length > 14 ? "preserveStartEnd" : 0}
                  angle={chartData.length > 10 ? -35 : 0}
                  textAnchor={chartData.length > 10 ? "end" : "middle"}
                  height={chartData.length > 10 ? 52 : 28}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: "#707070" }}
                  axisLine={false}
                  tickLine={false}
                  width={36}
                />
                <Tooltip
                  formatter={(value) => [`${value}`, "Cases"]}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ""}
                />
                <Line
                  type="monotone"
                  dataKey="cases"
                  name="Cases"
                  stroke={VELVET_WARM}
                  strokeWidth={2.4}
                  dot={
                    chartData.length > 60
                      ? false
                      : { r: 3, fill: VELVET_WARM, strokeWidth: 0 }
                  }
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default JuDailyCasesChart;
